import React, { useState, useRef, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './PlayerAIChat.css';
import { perplexityService } from '../../services/perplexityService';
import { useRateLimit } from '../../contexts/RateLimitContext';
import { useAuth } from '../../contexts/AuthContext';
import { secureAiChatHistoryService } from '../../services/secureAiChatHistoryService';

// Rate limit constants
const LIFETIME_USER_LIMIT = 5; // 5 requests total per user (lifetime)

// Types for chat messages
interface ChatMessage {
  id: string;
  content: string;
  role: 'user' | 'assistant';
  timestamp: Date;
  citations?: string[]; // Add citations array
}

// Component properties
interface PlayerAIChatProps {
  initialPosition?: 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
}

// Helper function to get current time as a formatted string
const getFormattedTime = (date: Date): string => {
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

// Helper function to extract citations from message content
const extractCitationsFromContent = (content: string): { cleanContent: string, citationData?: any } => {
  // Try to find a JSON block with citations at the end of the message
  const citationsMatch = content.match(/"citations":\s*\[([\s\S]*?)\],?/);
  
  if (citationsMatch) {
    try {
      // Parse the citation URLs
      const citationsArray = JSON.parse(`[${citationsMatch[1]}]`);
      
      // Clean the content by removing the citations data
      let cleanContent = content.replace(/"citations":\s*\[([\s\S]*?)\],?/, '').trim();
      
      // Also remove any trailing JSON formatting artifacts
      cleanContent = cleanContent.replace(/\}\s*$/, '').trim();
      
      return {
        cleanContent,
        citationData: { citations: citationsArray }
      };
    } catch (e) {
      console.error('Error parsing citations:', e);
    }
  }
  
  // If no citations found or parsing failed, return the original content
  return { cleanContent: content };
};

// Main PlayerAIChat component
const PlayerAIChat: React.FC<PlayerAIChatProps> = ({ 
  initialPosition = 'bottom-right' 
}) => {
  // Rate limiting and auth hooks
  const { isAiChatLimited, incrementAiChatCount, getRemainingCalls, isLoading: isRateLimitLoading, isAdmin, aiChatCount } = useRateLimit();
  const { isAuthenticated, supabaseUser } = useAuth();
  
  // State for chat management
  const [isExpanded, setIsExpanded] = useState<boolean>(false);
  // Initialize messages based on authentication status
  const getInitialMessage = (): ChatMessage => {
    if (!isAuthenticated) {
      return {
        id: 'initial',
        content: `# Welcome to Soccer Expert!

I can help with:
* Player stats and career information
* Team history and achievements
* Soccer tactics and formations
* Dream 11 team building advice

**🔒 Authentication Required**

Please sign up or log in to use the AI chat feature. Authenticated users get access to AI-powered soccer insights!

**Note:** Rate limits apply to prevent abuse.`,
        role: 'assistant',
        timestamp: new Date()
      };
    }

    const remainingCalls = getRemainingCalls();
    
    return {
      id: 'initial',
      content: `# Welcome to Soccer Expert!

I can help with:
* Player stats and career information
* Team history and achievements
* Soccer tactics and formations
* Dream 11 team building advice

${isAdmin ? 
  '**🔑 Admin Account**: You have unlimited AI chat queries!' : 
  `**Your Account**: You have ${remainingCalls} AI chat queries remaining.`
}

Ask me anything about soccer to help build your Dream 11 team!`,
      role: 'assistant',
      timestamp: new Date()
    };
  };

  const [messages, setMessages] = useState<ChatMessage[]>([getInitialMessage()]);
  const [inputMessage, setInputMessage] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [position, setPosition] = useState<string>(initialPosition);
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const [dragStartPos, setDragStartPos] = useState<{ x: number, y: number } | null>(null);
  const [elementPos, setElementPos] = useState<{ x: number, y: number }>({ x: 0, y: 0 });
  const [expandedCitations, setExpandedCitations] = useState<Record<string, boolean>>({});

  // Refs for DOM elements
  const chatRef = useRef<HTMLDivElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Handler for API key configuration status
  const [isApiConfigured, setIsApiConfigured] = useState<boolean>(perplexityService.isAvailable());

  // Update initial message when authentication status or rate limit changes
  useEffect(() => {
    console.log('🔄 Updating initial message:', {
      isAuthenticated,
      remaining: getRemainingCalls(),
      isAdmin,
      aiChatCount: getRemainingCalls(),
      currentMessagesLength: messages.length
    });
    
    // Only update initial message if we only have the initial message (no chat history)
    if (messages.length === 1 && messages[0].id === 'initial') {
      console.log('📝 Replacing initial message only');
      setMessages([getInitialMessage()]);
    } else {
      console.log('💬 Keeping existing chat history, messages count:', messages.length);
    }
  }, [isAuthenticated, isAdmin, aiChatCount]); // Changed dependency to aiChatCount instead of getRemainingCalls

  // Scroll to bottom of messages when new ones are added
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Initialize element position based on current position
  useEffect(() => {
    updatePositionFromCorner(position);
    
    // Add resize event listener
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [position]);

  // Handle window resize to ensure chat stays within viewport
  const handleResize = () => {
    updatePositionFromCorner(position);
  };

  // Update position based on corner
  const updatePositionFromCorner = (corner: string) => {
    if (!chatRef.current) return;
    
    const { innerWidth, innerHeight } = window;
    const margin = 20; // Margin from screen edges
    
    let x = 0, y = 0;
    
    switch (corner) {
      case 'top-left':
        x = margin;
        y = margin;
        break;
      case 'top-right':
        x = innerWidth - 60 - margin; // 60px is the width of collapsed chat
        y = margin;
        break;
      case 'bottom-left':
        x = margin;
        y = innerHeight - 60 - margin; // 60px is the height of collapsed chat
        break;
      case 'bottom-right':
      default:
        x = innerWidth - 60 - margin;
        y = innerHeight - 60 - margin;
        break;
    }
    
    setElementPos({ x, y });
  };

  // Mouse down handler to start dragging
  const handleMouseDown = (e: React.MouseEvent<HTMLButtonElement>) => {
    if (isExpanded) return; // Don't allow dragging when expanded
    
    setIsDragging(true);
    setDragStartPos({ x: e.clientX, y: e.clientY });
    
    // Prevent button click from registering right away
    e.preventDefault();
  };

  // Mouse move handler for dragging
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging || !dragStartPos) return;
    
    const deltaX = e.clientX - dragStartPos.x;
    const deltaY = e.clientY - dragStartPos.y;
    
    setElementPos(prevPos => ({
      x: prevPos.x + deltaX,
      y: prevPos.y + deltaY
    }));
    
    setDragStartPos({ x: e.clientX, y: e.clientY });
  };

  // Mouse up handler to stop dragging and snap to a corner
  const handleMouseUp = () => {
    if (!isDragging) return;
    
    setIsDragging(false);
    snapToCorner();
  };

  // Determine closest corner and snap to it
  const snapToCorner = () => {
    if (!chatRef.current) return;
    
    const { innerWidth, innerHeight } = window;
    const { x, y } = elementPos;
    
    // Determine which corner is closest
    const isTop = y < innerHeight / 2;
    const isLeft = x < innerWidth / 2;
    
    let newPosition: string;
    
    if (isTop) {
      newPosition = isLeft ? 'top-left' : 'top-right';
    } else {
      newPosition = isLeft ? 'bottom-left' : 'bottom-right';
    }
    
    setPosition(newPosition);
    updatePositionFromCorner(newPosition);
  };

  // Toggle chat expansion
  const toggleChat = (e: React.MouseEvent) => {
    // Only toggle if not dragging
    if (!isDragging) {
      setIsExpanded(prev => !prev);
      
      // Focus input when expanding
      if (!isExpanded) {
        setTimeout(() => {
          inputRef.current?.focus();
        }, 300);
      }
    }
  };

  // Toggle citation dropdown
  const toggleCitations = (messageId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    
    setExpandedCitations(prev => ({
      ...prev,
      [messageId]: !prev[messageId]
    }));
  };

  // Send message handler
  const sendMessage = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!inputMessage.trim() || isLoading) return;
    
    // Check if user is authenticated
    if (!isAuthenticated) {
      const authMessage: ChatMessage = {
        id: `auth-required-${Date.now()}`,
        content: `# Authentication Required

Please sign up or log in to use the AI chat feature.

Authenticated users get access to AI-powered soccer insights!`,
        role: 'assistant',
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, authMessage]);
      setInputMessage('');
      return;
    }
    
    // Check rate limit before processing
    if (isAiChatLimited) {
      const remainingCalls = getRemainingCalls();
      const limitMessage: ChatMessage = {
        id: `limit-${Date.now()}`,
        content: `# Rate Limit Reached

You've reached your rate limit for AI chat queries.

**Current Status:**
- Remaining queries: ${remainingCalls}
- Your limit will reset automatically

Please try again later when your rate limit resets.`,
        role: 'assistant',
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, limitMessage]);
      setInputMessage('');
      return;
    }
    
    // Try to increment the counter (async operation)
    const canIncrement = await incrementAiChatCount();
    if (!canIncrement) {
      const remainingCalls = getRemainingCalls();
      const limitMessage: ChatMessage = {
        id: `limit-${Date.now()}`,
        content: `# Rate Limit Reached

You've just reached your rate limit for AI chat queries.

**Current Status:**
- Remaining queries: ${remainingCalls}
- Your limit will reset automatically

Please try again later when your rate limit resets.`,
        role: 'assistant',
        timestamp: new Date()
      };
      
      setMessages(prev => [...prev, limitMessage]);
      setInputMessage('');
      return;
    }
    
    // Add user message
    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      content: inputMessage,
      role: 'user',
      timestamp: new Date()
    };
    
    console.log('📝 Adding user message:', userMessage);
    setMessages(prev => {
      const newMessages = [...prev, userMessage];
      console.log('📝 Messages after adding user message:', newMessages.length);
      return newMessages;
    });
    setInputMessage('');
    setIsLoading(true);
    
    try {
      // Convert existing messages to Perplexity format
      const messageHistory = messages
        .filter(msg => msg.id !== 'initial') // Skip initial greeting
        .map(msg => ({
          content: msg.content,
          role: msg.role
        }));
      
      // Add the new user message
      messageHistory.push({
        content: userMessage.content,
        role: 'user' as const
      });
      
      // Get API response
      const response = await perplexityService.chat(messageHistory);
      
      // Check if response has citations
      let responseText = '';
      let citations: string[] = [];
      
      if (typeof response === 'object' && response !== null) {
        // Handle structured response with citations
        const structuredResponse = response as any;
        responseText = structuredResponse.text || '';
        citations = structuredResponse.citations || [];
      } else {
        // Handle simple text response as string
        responseText = response as string;
        
        // Try to extract citations directly from the content
        const extracted = extractCitationsFromContent(responseText);
        responseText = extracted.cleanContent;
        
        if (extracted.citationData && extracted.citationData.citations) {
          citations = extracted.citationData.citations;
        }
      }
      
      // Add assistant response
      const assistantMessage: ChatMessage = {
        id: `assistant-${Date.now()}`,
        content: responseText,
        role: 'assistant',
        timestamp: new Date(),
        citations: citations
      };
      
      console.log('🤖 Adding assistant message:', assistantMessage);
      setMessages(prev => {
        const newMessages = [...prev, assistantMessage];
        console.log('🤖 Messages after adding assistant message:', newMessages.length);
        return newMessages;
      });
      
      // Save chat history to Supabase (if configured and user is authenticated)
      if (supabaseUser) {
        try {
          await secureAiChatHistoryService.saveChatInteraction(
            supabaseUser.id,
            userMessage.content,
            responseText,
            // Estimate tokens (rough calculation: 4 chars = 1 token)
            Math.ceil((userMessage.content.length + responseText.length) / 4)
          );
        } catch (error) {
          console.error('Failed to save chat history:', error);
          // Don't show error to user, just log it
        }
      }
      
      // Log for debugging
      console.log('Message with citations:', {
        content: responseText,
        citations: citations
      });
    } catch (error) {
      // Add error message
      const errorMessage: ChatMessage = {
        id: `error-${Date.now()}`,
        content: error instanceof Error 
          ? error.message 
          : 'Sorry, I encountered an error. Please try again later.',
        role: 'assistant',
        timestamp: new Date()
      };
      
      console.log('❌ Adding error message:', errorMessage);
      setMessages(prev => {
        const newMessages = [...prev, errorMessage];
        console.log('❌ Messages after adding error message:', newMessages.length);
        return newMessages;
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Calculate component styles
  const chatStyle: React.CSSProperties = {
    left: `${elementPos.x}px`,
    top: `${elementPos.y}px`
  };
  
  // Combine position classes
  const positionClasses = `player-ai-chat ${isExpanded ? 'expanded' : 'collapsed'} ${position} ${isDragging ? 'dragging' : ''}`;

  // Render citation list if available
  const renderCitations = (messageId: string, citations?: string[]) => {
    if (!citations || citations.length === 0) return null;
    
    const isExpanded = expandedCitations[messageId] || false;
    
    return (
      <div className="message-citations">
        <button 
          type="button"
          className="citations-toggle" 
          onClick={(e) => toggleCitations(messageId, e)}
        >
          {isExpanded ? 'Hide sources' : `Show sources (${citations.length})`}
          <span className={`toggle-icon ${isExpanded ? 'expanded' : ''}`}>
            {isExpanded ? '▼' : '▶'}
          </span>
        </button>
        
        {isExpanded && (
          <div className="citations-content">
            <ol className="citations-list">
              {citations.map((url, index) => (
                <li key={index}>
                  <a 
                    href={url} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="citation-link"
                  >
                    {(() => {
                      try {
                        return new URL(url).hostname.replace('www.', '');
                      } catch (e) {
                        return url;
                      }
                    })()}
                  </a>
                </li>
              ))}
            </ol>
          </div>
        )}
      </div>
    );
  };

  return (
    <div 
      ref={chatRef}
      className={positionClasses}
      style={chatStyle}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
    >
      <button 
        className="chat-toggle-btn"
        onMouseDown={handleMouseDown}
        onClick={toggleChat}
      >
        {isExpanded ? (
          <span className="minimize-icon">−</span>
        ) : (
          <div className="chat-icon">
            <div className="chat-icon-circle">
              <span>AI</span>
            </div>
            <div className="tooltip">Soccer Assistant</div>
          </div>
        )}
      </button>
      
      {isExpanded && (
        <div className={`chat-container ${position}`}>
          <div className="chat-header">
            <h3>Soccer Team Assistant</h3>
            <div className="rate-limit-indicator">
              {!isAuthenticated ? (
                <span className="auth-required">🔒 Sign in required</span>
              ) : isAdmin ? (
                <span className="admin-status">🔑 Admin - Unlimited</span>
              ) : isAiChatLimited ? (
                <span className="limit-reached">Lifetime limit reached</span>
              ) : (
                <span className="queries-remaining">
                  {getRemainingCalls()}/{LIFETIME_USER_LIMIT} queries left
                </span>
              )}
            </div>
          </div>
          
          <div className="chat-messages">
            {messages.map(message => (
              <div key={message.id} className={`chat-message ${message.role}`}>
                <div className="message-content">
                  <ReactMarkdown 
                    remarkPlugins={[remarkGfm]}
                    components={{
                      // Style code blocks
                      code({className, children, ...props}: any) {
                        return (
                          <code className={className} {...props}>
                            {children}
                          </code>
                        )
                      },
                      // Style links
                      a({children, href, ...props}: any) {
                        return (
                          <a 
                            href={href} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="chat-link"
                            {...props}
                          >
                            {children}
                          </a>
                        )
                      }
                    }}
                  >
                    {message.content}
                  </ReactMarkdown>
                </div>
                <div className="message-time">{getFormattedTime(message.timestamp)}</div>
                {message.citations && message.citations.length > 0 && 
                  renderCitations(message.id, message.citations)}
              </div>
            ))}
            
            {isLoading && (
              <div className="chat-message loading">
                <div className="loading-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
          
          <form className="chat-input-form" onSubmit={sendMessage}>
            <input
              ref={inputRef}
              type="text"
              className="chat-input"
              placeholder={
                !isAuthenticated 
                  ? "Sign in to use AI chat..." 
                  : isAiChatLimited 
                    ? "Lifetime limit reached..."
                    : isRateLimitLoading
                      ? "Loading..."
                      : "Ask about players, tactics, formations..."
              }
              value={inputMessage}
              onChange={(e) => setInputMessage(e.target.value)}
              disabled={isLoading || isRateLimitLoading || !isApiConfigured || !isAuthenticated || isAiChatLimited}
            />
            <button 
              type="submit" 
              className="send-btn"
              disabled={!inputMessage.trim() || isLoading || isRateLimitLoading || !isApiConfigured || !isAuthenticated || isAiChatLimited}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M2.01 21L23 12 2.01 3 2 10L17 12 2 14z" fill="white"/>
              </svg>
            </button>
          </form>
          
          {!isAuthenticated && (
            <div className="auth-warning">
              Please sign up or log in to use the AI chat feature. Each user gets {LIFETIME_USER_LIMIT} queries total!
            </div>
          )}
          
          {!isApiConfigured && (
            <div className="api-warning">
              Perplexity API key not configured. Add REACT_APP_PERPLEXITY_API_KEY to your .env file.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default PlayerAIChat; 